import { L3_PATHS } from 'Deer/constants/routes';
import { getUserAccess, getUserL1Access } from 'Deer/helpers/getUserAccess';
import {
  checkGoalModuleEnabled,
  checkReviewModuleEnabled,
  checkOneOnOneModuleEnabled,
  checkFeedbackModuleEnabled,
  checkTaskModuleEnabled,
  checkEngagementModuleEnabled,
  checkDisciplinaryModuleEnabled,
} from 'utils/helpers/moduleAccess';
import {
  checkUserIsGoalAdmin,
  checkUserIsReviewAdmin,
  checkUserIsOneOnOneAdmin,
  checkUserIsFeedbackAdmin,
  checkUserIsEngagementAdmin,
  checkUserIsDisciplinaryActionsAdmin,
} from 'utils/helpers/rolesAccess';

export const DATA_FOR_OPTIONS: {
  label: string;
  value: 'myData' | 'team' | 'company';
  type: 'DEFAULT' | 'RADIO';
  options?: {
    label: string;
    value: 'directReports' | 'allReports';
    visible?: () => boolean;
  }[];
  visible: () => boolean;
}[] = [
  {
    label: 'Me',
    value: 'myData',
    type: 'DEFAULT',
    visible: () => true,
  },
  {
    label: 'Team',
    value: 'team',
    type: 'RADIO',
    options: [
      {
        label: 'Direct Team',
        value: 'directReports',
        visible: () => getUserAccess().isDirectManager,
      },
      {
        label: 'Skip Team',
        value: 'allReports',
        visible: () => getUserAccess().isSkipManager,
      },
    ],
    visible: () => getUserL1Access().hasL1TeamAccess,
  },
  {
    label: 'Company',
    value: 'company',
    type: 'DEFAULT',
    visible: () => getUserL1Access().hasL1CompanyAccess,
  },
];

export const DEFAULT_INTENTS_FOR_MODULES = {
  goal: {
    myData: {
      path: L3_PATHS['my-data'].goal.myGoals,
      isAccessible: checkGoalModuleEnabled,
    },
    team: {
      directReports: {
        path: L3_PATHS.team.directReports.goal.manage,
        isAccessible: checkGoalModuleEnabled,
      },
      allReports: {
        path: L3_PATHS.team.allReports.goal.manage,
        isAccessible: checkGoalModuleEnabled,
      },
      view: {
        path: L3_PATHS.team.view.goal.manage,
        isAccessible: checkGoalModuleEnabled,
      },
    },
    company: {
      path: L3_PATHS.company.goal.manage,
      isAccessible: () => checkUserIsGoalAdmin() || getUserAccess()?.isCustomGoalAdmin,
    },
  },
  review: {
    myData: {
      path: L3_PATHS['my-data'].review.myReviews,
      isAccessible: checkReviewModuleEnabled,
    },
    team: {
      directReports: {
        path: L3_PATHS.team.directReports.review.completionStatus,
        isAccessible: checkReviewModuleEnabled,
      },
      allReports: {
        path: L3_PATHS.team.allReports.review.completionStatus,
        isAccessible: checkReviewModuleEnabled,
      },
      view: {
        path: L3_PATHS.team.view.review.completionStatus,
        isAccessible: checkReviewModuleEnabled,
      },
    },
    company: {
      path: L3_PATHS.company.review.completionStatus,
      isAccessible: checkUserIsReviewAdmin || getUserAccess()?.isCustomReviewAdmin,
    },
  },
  oneOnOne: {
    myData: {
      path: L3_PATHS['my-data'].oneOnOne.myOneOnOne,
      isAccessible: checkOneOnOneModuleEnabled,
    },
    team: {
      directReports: {
        path: L3_PATHS.team.directReports.oneOnOne.manage,
        isAccessible: checkOneOnOneModuleEnabled,
      },
      allReports: {
        path: L3_PATHS.team.allReports.oneOnOne.manage,
        isAccessible: checkOneOnOneModuleEnabled,
      },
    },
    company: {
      path: L3_PATHS.company.oneOnOne.insights,
      isAccessible: () => checkUserIsOneOnOneAdmin() || getUserAccess()?.isCustomOneOnOneAdmin,
    },
  },
  develop: {
    myData: {
      path: L3_PATHS['my-data'].develop.competencies,
      isAccessible: checkFeedbackModuleEnabled,
    },
    team: {
      directReports: {
        path: L3_PATHS.team.directReports.develop.manage,
        isAccessible: checkFeedbackModuleEnabled,
      },
      allReports: {
        path: L3_PATHS.team.allReports.develop.insights,
        isAccessible: checkFeedbackModuleEnabled,
      },
    },
    company: {
      path: L3_PATHS.company.develop.insights,
      isAccessible: () => checkUserIsFeedbackAdmin() || getUserAccess()?.isCustomFeedbackAdmin,
    },
  },
  initiative: {
    myData: {
      path: L3_PATHS['my-data'].initiative.myInitiatives,
      isAccessible: () => checkTaskModuleEnabled(),
    },
  },
  engagement: {
    myData: {
      path: L3_PATHS['my-data'].engagement.mySurveys,
      isAccessible: checkEngagementModuleEnabled,
    },
    team: {
      directReports: {
        path: L3_PATHS.team.directReports.engagement.insights,
        isAccessible: checkEngagementModuleEnabled,
      },
      allReports: {
        path: L3_PATHS.team.allReports.engagement.insights,
        isAccessible: checkEngagementModuleEnabled,
      },
    },
    company: {
      path: L3_PATHS.company.engagement.insights,
      isAccessible: () => checkUserIsEngagementAdmin() || getUserAccess()?.isCustomEngagementAdmin,
    },
  },
  disciplinaryActions: {
    myData: {
      path: L3_PATHS['my-data'].disciplinaryActions.pendingDisciplinaryActions,
      isAccessible: checkDisciplinaryModuleEnabled,
    },
    team: {
      directReports: {
        path: L3_PATHS.team.directReports.disciplinaryActions.pendingDisciplinaryActions,
        isAccessible: checkDisciplinaryModuleEnabled,
      },
      allReports: {
        path: L3_PATHS.team.allReports.disciplinaryActions.pendingDisciplinaryActions,
        isAccessible: checkDisciplinaryModuleEnabled,
      },
    },
    company: {
      path: L3_PATHS.company.disciplinaryActions.pendingDisciplinaryActions,
      isAccessible: () => checkUserIsDisciplinaryActionsAdmin() || getUserAccess()?.isCustomDisciplinaryActionsAdmin,
    },
  },
};
